import { useState, useEffect } from 'react';
import { Sparkles, Loader2, TrendingUp, AlertCircle, FileText, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { generateAnnotatedImage } from '@/utils/imageAnnotator';
import { generateSingleAnalysisWord } from '@/utils/wordGenerator';
import { generatePDFFromHTML } from '@/utils/pdfGenerator';
import ShareButton from '@/components/ShareButton';
import demoPersonaData from '@/data/DEMO_PERSONA_DATA.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to parse and format markdown text
const formatMarkdownText = (text) => {
  if (!text) return null;
  
  // Split into sentences or bullet points
  const parts = text.split(/\n|(?<=\.)\s+(?=[A-Z])/);
  
  return parts.map((part, idx) => {
    if (!part.trim()) return null;
    
    // Remove ** and make those parts bold
    const formatted = part.split(/\*\*(.+?)\*\*/g).map((segment, i) => {
      if (i % 2 === 1) {
        // Odd indices are the text that was between **
        return <strong key={i} className="font-bold">{segment}</strong>;
      }
      return segment;
    });
    
    return (
      <li key={idx} className="mb-2">
        {formatted}
      </li>
    );
  }).filter(Boolean);
};

const AIEnhancement = ({ analysis, darkMode = false, onCreditsUpdate, isDemoAnalysis = false, demoPersonaKey = null, demoEnhancement = null, user = null, authToken = null }) => {
  const [loading, setLoading] = useState(false);
  const [enhancement, setEnhancement] = useState(null);
  const [annotatedImage, setAnnotatedImage] = useState(null);
  const [generatingAnnotation, setGeneratingAnnotation] = useState(false);
  
  // Super Enhancer state - Image: Photo and Creative
  const [photoEnhancing, setPhotoEnhancing] = useState(false);
  const [creativeEnhancing, setCreativeEnhancing] = useState(false);
  const [photoEnhancement, setPhotoEnhancement] = useState(null);
  const [activeEditPlatform, setActiveEditPlatform] = useState('iphone');
  const [creativeEnhancement, setCreativeEnhancement] = useState(null);
  
  // Super Enhancer state - Text: Tone Optimizer
  const [textToneEnhancing, setTextToneEnhancing] = useState(false);
  const [textToneEnhancement, setTextToneEnhancement] = useState(null);
  
  // Super Enhancer state - Video: Caption Generator
  const [videoCaptionEnhancing, setVideoCaptionEnhancing] = useState(false);
  const [videoCaptionEnhancement, setVideoCaptionEnhancement] = useState(null);
  
  // Active tabs for different content types
  const [activeImageTab, setActiveImageTab] = useState('photo'); // 'photo' or 'creative'
  const [activeTextTab, setActiveTextTab] = useState('professional'); // 'professional', 'persuasive', or 'empathetic'
  const [activeVideoTab, setActiveVideoTab] = useState('minimal'); // 'minimal', 'engaging', or 'dramatic'
  
  // Debug: Log authToken changes
  useEffect(() => {
    console.log('🔐 AIEnhancement authToken changed:', {
      authTokenProp: authToken ? 'exists (length: ' + authToken.length + ')' : 'null/undefined',
      localStorage: localStorage.getItem('authToken') ? 'exists' : 'missing',
      user: user ? user.email : 'no user'
    });
  }, [authToken, user]);
  
  // Check if enhancement already exists (including demo enhancement)
  useEffect(() => {
    if (demoEnhancement && isDemoAnalysis) {
      // Use pre-generated demo enhancement data
      setEnhancement(demoEnhancement);
      console.log('🎭 Using pre-generated demo enhancement data');
    } else if (analysis?.enhancement) {
      setEnhancement(analysis.enhancement);
      // If there's a saved annotated image, use it
      if (analysis.enhancement.annotated_image) {
        setAnnotatedImage(analysis.enhancement.annotated_image);
      }
    }
    
    // Check if super enhancements already exist
    if (analysis?.super_enhancement_photo) {
      setPhotoEnhancement(analysis.super_enhancement_photo);
      console.log('📸 Loading existing photo enhancement');
    }
    if (analysis?.super_enhancement_creative) {
      setCreativeEnhancement(analysis.super_enhancement_creative);
      console.log('🎨 Loading existing creative enhancement');
    }
    if (analysis?.super_enhancement_text_tone) {
      setTextToneEnhancement(analysis.super_enhancement_text_tone);
      console.log('📝 Loading existing text tone enhancement');
    }
    if (analysis?.super_enhancement_video_captions) {
      setVideoCaptionEnhancement(analysis.super_enhancement_video_captions);
      console.log('🎬 Loading existing video caption enhancement');
    }
  }, [analysis, demoEnhancement, isDemoAnalysis]);

  // Calculate enhancement cost (flat rates)
  const getEnhancementCost = () => {
    const contentType = analysis?.content_type || 'text';
    // Flat rates: 10 credits for most features, 20 for document
    if (contentType === 'document') return 20;
    return 10; // text, image, url, video, compare
  };

  const cost = getEnhancementCost();

  const handleEnhance = async () => {
    if (!analysis) {
      toast.error('No analysis data available');
      return;
    }

    // Check if this is a demo analysis (from prop OR from analysis data)
    const isDemo = isDemoAnalysis || analysis.is_demo_analysis;
    const demoKey = demoPersonaKey || analysis.demo_persona_key;

    // Check authentication and credits before enhancement (skip for demo)
    if (!isDemo) {
      // Check if user is authenticated - check token first (most reliable)
      const token = authToken || localStorage.getItem('authToken');
      
      if (!token) {
        console.log('⚠️ User not authenticated - no token found');
        toast.error('Please log in to use content enhancement. Your session may have expired.');
        return;
      }
      
      console.log('✅ User authenticated (token exists), checking credits...');
      
      // Check credits if user object is available
      if (user && user.credits !== undefined && user.credits !== null) {
        const userCredits = Number(user.credits);
        
        console.log('💳 Enhancement credit check:', {
          userCreditsNumber: userCredits,
          costNeeded: cost,
          hasEnough: userCredits >= cost
        });

        if (userCredits < cost) {
          console.log('❌ Insufficient credits for enhancement');
          toast.error(`Insufficient credits. You need ${cost} credits but have ${userCredits}.`);
          return;
        }
        console.log('✅ Enhancement credits check passed');
      } else {
        // If credits not loaded yet, proceed anyway (backend will validate)
        console.log('⚠️ User credits not loaded yet, backend will validate');
      }
    }

    setLoading(true);
    try {
      // Check if this is a demo analysis
      if (isDemo && (demoEnhancement || (demoKey && demoPersonaData[demoKey]))) {
        console.log('🎭 Using pre-generated demo enhancement');
        
        // Simulate realistic loading time (10-15 seconds)
        await new Promise(resolve => setTimeout(resolve, 10000 + Math.random() * 5000));
        
        // Use provided demoEnhancement or fetch from demoPersonaData
        const enhancementData = demoEnhancement || (demoKey && demoPersonaData[demoKey]?.enhancement);
        setEnhancement(enhancementData);
        toast.success('EmoQ Enhancement generated successfully!');
        
        console.log('✅ Demo enhancement loaded successfully');
      } else {
        // Regular API call for non-demo analyses
        // Get token if available (for JWT auth), but also rely on cookies (for OAuth)
        const token = authToken || localStorage.getItem('authToken');
        const headers = {
          'Content-Type': 'application/json'
        };
        
        // Add Authorization header if JWT token exists
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log('🔑 Sending enhancement request with JWT token (length:', token.length, ')');
        } else {
          console.log('🍪 Sending enhancement request with cookie auth (OAuth flow)');
        }
        
        console.log('📤 Sending enhancement request:', {
          analysis_id: analysis.id,
          content_type: analysis.content_type,
          authMethod: token ? 'JWT' : 'Cookie',
          withCredentials: true,
          user: user ? { id: user.id, email: user.email, credits: user.credits } : 'no user',
          tokenInHeaders: !!headers['Authorization']
        });
        
        const response = await axios.post(`${API}/enhance/content`, {
          analysis_id: analysis.id,
          content_type: analysis.content_type,
          original_content: analysis.content,
          content_preview: analysis.content_preview,
          custom_question: analysis.custom_question,
          emotions: analysis.emotions,
          audience_reactions: analysis.audience_reactions,
          target_audience: analysis.target_audience,
          recommendations: analysis.recommendations,
          is_comparison: analysis.is_comparison || false,
          is_best_of_three: analysis.is_best_of_three || false
        }, {
          headers,
          withCredentials: true,
          timeout: 90000 // 90 seconds
        });

        console.log('✅ Enhancement response received');
        console.log('Enhancement data:', response.data);
        console.log('Annotation points:', response.data?.annotation_points);
        setEnhancement(response.data);
        toast.success('EmoQ Enhancement generated successfully!');
        
        // Update credits after enhancement
        if (onCreditsUpdate) {
          await onCreditsUpdate();
        }
        
        // Generate annotated image if image content and has annotation points (only for non-video content)
        if (analysis.content_type !== 'video' && analysis.image_data && response.data?.annotation_points && response.data.annotation_points.length > 0) {
          console.log('Starting annotation generation with', response.data.annotation_points.length, 'points');
          generateAnnotatedImageAsync(response.data.annotation_points);
        } else {
          console.log('Skipping annotation - content_type:', analysis.content_type, 'image_data:', !!analysis.image_data, 'annotation_points:', response.data?.annotation_points);
        }
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      
      // More specific error handling
      if (error.response?.status === 401) {
        toast.error('Please log in to use content enhancement. Your session may have expired.');
      } else if (error.response?.status === 402) {
        // Insufficient credits
        const errorMsg = error.response?.data?.detail || 'Insufficient credits for enhancement';
        toast.error(errorMsg);
      } else if (error.response?.status === 422) {
        // Validation error
        toast.error('Invalid request data. Please try again.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate enhancement';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateAnnotatedImageAsync = async (annotationPoints) => {
    if (!analysis.image_data) {
      console.warn('No image_data available for annotation');
      return;
    }
    
    if (!annotationPoints || annotationPoints.length === 0) {
      console.warn('No annotation points provided - skipping visual annotation');
      toast.info('Visual annotations not available for this analysis');
      return;
    }
    
    setGeneratingAnnotation(true);
    try {
      console.log('Generating annotated image with points:', annotationPoints);
      const annotated = await generateAnnotatedImage(analysis.image_data, annotationPoints);
      setAnnotatedImage(annotated);
      
      // Save annotated image to backend
      await axios.post(`${API}/enhance/save-annotation`, {
        analysis_id: analysis.id,
        annotated_image: annotated
      }, {
        withCredentials: true
      });
      
      toast.success('Visual annotation created!');
    } catch (error) {
      console.error('Annotation error:', error);
      toast.error('Failed to create visual annotations: ' + (error.message || 'Unknown error'));
    } finally {
      setGeneratingAnnotation(false);
    }
  };

  const handlePhotoEnhance = async () => {
    if (!analysis || !enhancement) {
      toast.error('Please generate content enhancement first');
      return;
    }

    if (!analysis.image_data) {
      toast.error('Photo Enhancement only works with image content');
      return;
    }

    // Check authentication
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      toast.error('Please log in to use Photo Enhancement');
      return;
    }

    // Check credits
    const PHOTO_ENHANCE_COST = 15;
    if (user && user.credits !== undefined) {
      const userCredits = Number(user.credits);
      if (userCredits < PHOTO_ENHANCE_COST) {
        toast.error(`Insufficient credits. Photo Enhancement requires ${PHOTO_ENHANCE_COST} credits but you have ${userCredits}.`);
        return;
      }
    }

    setPhotoEnhancing(true);
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      toast.loading('Enhancing your photo... This may take 30-60 seconds.', { id: 'photo-enhance' });

      const response = await axios.post(`${API}/super-enhance/photo`, {
        analysis_id: analysis.id,
        original_image_base64: analysis.image_data
      }, {
        headers,
        withCredentials: true,
        timeout: 90000
      });

      console.log('✅ Photo Enhancement complete:', response.data);
      setPhotoEnhancement(response.data);
      setActiveTab('photo');
      toast.success('Photo enhanced successfully! 📸', { id: 'photo-enhance' });

      // Update credits
      if (onCreditsUpdate) {
        await onCreditsUpdate();
      }

    } catch (error) {
      console.error('Photo Enhancement error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to use Photo Enhancement', { id: 'photo-enhance' });
      } else if (error.response?.status === 402) {
        const errorMsg = error.response?.data?.detail || 'Insufficient credits';
        toast.error(errorMsg, { id: 'photo-enhance' });
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.detail || 'Please generate content enhancement first';
        toast.error(errorMsg, { id: 'photo-enhance' });
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to enhance photo';
        toast.error(errorMsg, { id: 'photo-enhance' });
      }
    } finally {
      setPhotoEnhancing(false);
    }
  };

  const handleCreativeEnhance = async () => {
    if (!analysis || !enhancement) {
      toast.error('Please generate content enhancement first');
      return;
    }

    if (!analysis.image_data) {
      toast.error('EmoQ AI Creative only works with image content');
      return;
    }

    // Check authentication
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      toast.error('Please log in to use EmoQ AI Creative');
      return;
    }

    // Check credits
    const CREATIVE_COST = 20;
    if (user && user.credits !== undefined) {
      const userCredits = Number(user.credits);
      if (userCredits < CREATIVE_COST) {
        toast.error(`Insufficient credits. EmoQ AI Creative requires ${CREATIVE_COST} credits but you have ${userCredits}.`);
        return;
      }
    }

    setCreativeEnhancing(true);
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      toast.loading('Creating AI artistic interpretation... This may take up to 1 minute.', { id: 'creative-enhance' });

      const response = await axios.post(`${API}/super-enhance/creative`, {
        analysis_id: analysis.id,
        original_image_base64: analysis.image_data
      }, {
        headers,
        withCredentials: true,
        timeout: 90000
      });

      console.log('✅ Creative Enhancement complete:', response.data);
      setCreativeEnhancement(response.data);
      setActiveTab('creative');
      toast.success('AI Creative interpretation generated! 🎨', { id: 'creative-enhance' });

      // Update credits
      if (onCreditsUpdate) {
        await onCreditsUpdate();
      }

    } catch (error) {
      console.error('Creative Enhancement error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to use EmoQ AI Creative', { id: 'creative-enhance' });
      } else if (error.response?.status === 402) {
        const errorMsg = error.response?.data?.detail || 'Insufficient credits';
        toast.error(errorMsg, { id: 'creative-enhance' });
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.detail || 'Please generate content enhancement first';
        toast.error(errorMsg, { id: 'creative-enhance' });
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate creative version';
        toast.error(errorMsg, { id: 'creative-enhance' });
      }
    } finally {
      setCreativeEnhancing(false);
    }
  };

  const handleTextToneEnhance = async () => {
    if (!analysis || !enhancement) {
      toast.error('Please generate content enhancement first');
      return;
    }

    // Check authentication
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      toast.error('Please log in to use Text Tone Optimizer');
      return;
    }

    // Check credits
    const TEXT_TONE_COST = 12;
    if (user && user.credits !== undefined) {
      const userCredits = Number(user.credits);
      if (userCredits < TEXT_TONE_COST) {
        toast.error(`Insufficient credits. Text Tone Optimizer requires ${TEXT_TONE_COST} credits but you have ${userCredits}.`);
        return;
      }
    }

    setTextToneEnhancing(true);
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      toast.loading('Generating tone-optimized versions... This may take 30-45 seconds.', { id: 'text-tone-enhance' });

      const response = await axios.post(`${API}/super-enhance/text-tone`, {
        analysis_id: analysis.id,
        original_image_base64: '' // Not needed for text
      }, {
        headers,
        withCredentials: true,
        timeout: 60000
      });

      console.log('✅ Text Tone Enhancement complete:', response.data);
      setTextToneEnhancement(response.data);
      setActiveTextTab('professional');
      toast.success('Text variations generated successfully! 📝', { id: 'text-tone-enhance' });

      // Update credits
      if (onCreditsUpdate) {
        await onCreditsUpdate();
      }

    } catch (error) {
      console.error('Text Tone Enhancement error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to use Text Tone Optimizer', { id: 'text-tone-enhance' });
      } else if (error.response?.status === 402) {
        const errorMsg = error.response?.data?.detail || 'Insufficient credits';
        toast.error(errorMsg, { id: 'text-tone-enhance' });
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.detail || 'Please generate content enhancement first';
        toast.error(errorMsg, { id: 'text-tone-enhance' });
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate tone variations';
        toast.error(errorMsg, { id: 'text-tone-enhance' });
      }
    } finally {
      setTextToneEnhancing(false);
    }
  };

  const handleVideoCaptionEnhance = async () => {
    if (!analysis || !enhancement) {
      toast.error('Please generate content enhancement first');
      return;
    }

    // Check authentication
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      toast.error('Please log in to use Video Caption Generator');
      return;
    }

    // Check credits
    const VIDEO_CAPTION_COST = 15;
    if (user && user.credits !== undefined) {
      const userCredits = Number(user.credits);
      if (userCredits < VIDEO_CAPTION_COST) {
        toast.error(`Insufficient credits. Video Caption Generator requires ${VIDEO_CAPTION_COST} credits but you have ${userCredits}.`);
        return;
      }
    }

    setVideoCaptionEnhancing(true);
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      toast.loading('Generating video captions... This may take 30-45 seconds.', { id: 'video-caption-enhance' });

      const response = await axios.post(`${API}/super-enhance/video-captions`, {
        analysis_id: analysis.id,
        original_image_base64: '' // Not needed for video
      }, {
        headers,
        withCredentials: true,
        timeout: 60000
      });

      console.log('✅ Video Caption Enhancement complete:', response.data);
      setVideoCaptionEnhancement(response.data);
      setActiveVideoTab('minimal');
      toast.success('Video captions generated successfully! 🎬', { id: 'video-caption-enhance' });

      // Update credits
      if (onCreditsUpdate) {
        await onCreditsUpdate();
      }

    } catch (error) {
      console.error('Video Caption Enhancement error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to use Video Caption Generator', { id: 'video-caption-enhance' });
      } else if (error.response?.status === 402) {
        const errorMsg = error.response?.data?.detail || 'Insufficient credits';
        toast.error(errorMsg, { id: 'video-caption-enhance' });
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.detail || 'Please generate content enhancement first';
        toast.error(errorMsg, { id: 'video-caption-enhance' });
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate captions';
        toast.error(errorMsg, { id: 'video-caption-enhance' });
      }
    } finally {
      setVideoCaptionEnhancing(false);
    }
  };

  const handleCopyText = async (text, toneName) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${toneName} version copied to clipboard!`);
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy text');
    }
  };

  const handleDownloadSRT = (srtContent, styleName) => {
    try {
      const blob = new Blob([srtContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `captions-${styleName}-${analysis.id}.srt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${styleName} SRT file downloaded!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download SRT file');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.loading('Generating PDF with enhancement...', { id: 'pdf-enhance' });
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if element exists before attempting PDF generation
      const element = document.getElementById('analysis-results-pdf');
      if (!element) {
        throw new Error('Analysis results not found. Please ensure the analysis is visible.');
      }
      
      // Use the main analysis results container that includes enhancement
      await generatePDFFromHTML('analysis-results-pdf', `emoQ-enhanced-${analysis.content_type}`);
      toast.success('PDF downloaded successfully!', { id: 'pdf-enhance' });
    } catch (error) {
      console.error('PDF error:', error);
      toast.error(error.message || 'Failed to generate PDF', { id: 'pdf-enhance' });
      // Don't navigate or throw - just show error
    }
  };

  const handleDownloadWord = async () => {
    try {
      // Create enhanced analysis object
      const enhancedAnalysis = {
        ...analysis,
        enhancement: enhancement,
        annotated_image: annotatedImage
      };
      await generateSingleAnalysisWord(enhancedAnalysis);
      toast.success('Word document downloaded!');
    } catch (error) {
      console.error('Word error:', error);
      toast.error('Failed to generate Word document');
    }
  };

  return (
    <Card className={`${darkMode ? 'bg-gradient-to-br from-purple-900 to-pink-900 border-purple-700' : 'bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 border-purple-300'} shadow-xl`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Sparkles className="w-5 h-5 text-purple-600" />
              EmoQ Content Enhancement
            </CardTitle>
            <CardDescription className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
              Get EmoQ-powered suggestions and optimized content to boost your emotional impact
            </CardDescription>
          </div>
          {!enhancement && (
            <Button
              onClick={handleEnhance}
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-6 py-3 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span className="text-base">Generating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  <span className="text-base">Enhance</span>
                  {(isDemoAnalysis || analysis?.is_demo_analysis) ? (
                    <span className="text-xs ml-1 opacity-90">(FREE DEMO)</span>
                  ) : (
                    <span className="text-xs ml-1 opacity-90">({cost} credits)</span>
                  )}
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>

      {!enhancement && !loading && (
        <CardContent>
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-purple-800/30 border-purple-600' : 'bg-white/80 border-purple-300'} border shadow-md`}>
            <div className="flex items-start gap-3">
              <TrendingUp className={`w-5 h-5 flex-shrink-0 mt-1 ${darkMode ? 'text-purple-300' : 'text-purple-600'}`} />
              <div>
                <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  What you'll get:
                </h4>
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  <li>✨ Enhanced content optimized for your goals</li>
                  <li>📊 Predicted improvement in emotion scores</li>
                  <li>🎯 Strategic recommendations based on analysis</li>
                  <li>💡 Platform-specific optimization tips</li>
                  {analysis?.content_type === 'image' && (
                    <li>🎨 Visual improvement guidelines (composition, colors, elements)</li>
                  )}
                </ul>
                <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-purple-600' : 'border-purple-300'}`}>
                  <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    {(isDemoAnalysis || analysis?.is_demo_analysis) ? (
                      <>
                        <span className="font-semibold text-green-500">FREE DEMO</span> • No credits required for demo personas
                      </>
                    ) : (
                      <>
                        Cost: <span className="font-semibold">{cost} credits</span> • 
                        {analysis?.testing_mode ? ' Testing mode active - no real charges' : ' Real credits will be deducted'}
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}

      {enhancement && (
        <CardContent className="space-y-6">
          {/* Video/Image Thumbnail */}
          {analysis?.image_data && (
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800/90 border-2 border-purple-500' : 'bg-white border border-gray-200'}`}>
              <img 
                src={analysis.image_data.startsWith('data:') ? analysis.image_data : `data:image/jpeg;base64,${analysis.image_data}`}
                alt="Content thumbnail"
                className="w-full max-h-48 object-contain rounded-lg"
              />
            </div>
          )}
          
          {/* Enhanced Content Section */}
          <div className={`p-6 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800/90 border-2 border-purple-500 shadow-purple-500/20' : 'bg-white border border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {analysis?.content_type === 'image' ? '🎨 Visual Improvement Guidelines' : '✨ Enhanced Content'}
              </h4>
              <Badge className="bg-green-100 text-green-700">Optimized</Badge>
            </div>
            <div className={`p-4 rounded border ${darkMode ? 'bg-gray-700/50 border-purple-500/30 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>
              {enhancement.enhanced_content ? (
                <ul className="space-y-2 leading-relaxed text-base">
                  {formatMarkdownText(enhancement.enhanced_content)}
                </ul>
              ) : (
                <p className="whitespace-pre-wrap leading-relaxed text-base">
                  Enhancement content is being processed. Please try again in a moment.
                </p>
              )}
            </div>
            
            {/* Show original and annotated images side by side */}
            {(analysis.image_data || annotatedImage || enhancement.annotated_image || generatingAnnotation) && (
              <div className="mt-4 space-y-4">
                {/* Original Image */}
                {analysis.image_data && (
                  <div>
                    <h5 className="text-sm font-semibold mb-2 text-gray-700">
                      Original Image:
                    </h5>
                    <img 
                      src={analysis.image_data.startsWith('data:') ? analysis.image_data : `data:image/jpeg;base64,${analysis.image_data}`}
                      alt="Original content" 
                      className="w-full rounded-lg border border-gray-300"
                    />
                  </div>
                )}
                
                {/* Annotated Image */}
                {generatingAnnotation ? (
                  <div className="flex items-center justify-center p-8 bg-gray-100 rounded-lg">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    <span className="text-gray-700">Creating visual annotations...</span>
                  </div>
                ) : (annotatedImage || enhancement.annotated_image) ? (
                  <div>
                    <h5 className="text-sm font-semibold mb-2 text-gray-700">
                      Annotated Image with Suggestions:
                    </h5>
                    <img 
                      src={annotatedImage || enhancement.annotated_image} 
                      alt="Annotated with suggestions" 
                      className="w-full rounded-lg border-2 border-purple-500"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Strategic Recommendations with Numbers */}
          <div className={`p-6 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800/90 border-2 border-purple-500 shadow-purple-500/20' : 'bg-white border border-gray-200'}`}>
            <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              🎯 Strategic Recommendations {annotatedImage && '(Match numbers on image above)'}
            </h4>
            <div className="space-y-3">
              {enhancement.suggestions?.map((suggestion, idx) => (
                <div key={idx} className={`p-4 rounded-lg border-l-4 flex gap-3 ${darkMode ? 'bg-purple-900/30 border-purple-500' : 'bg-purple-50 border-purple-600'}`}>
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">
                      {idx + 1}
                    </div>
                  </div>
                  <div className={`leading-relaxed flex-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    <ul className="space-y-1">
                      {formatMarkdownText(suggestion)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Predicted Improvements - Only Positive Emotions */}
          {enhancement.predicted_scores && Object.keys(enhancement.predicted_scores).length > 0 && (
            <div className={`p-6 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800/90 border-2 border-purple-500 shadow-purple-500/20' : 'bg-white border border-gray-200'}`}>
              <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                📈 Predicted Emotional Improvements
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(enhancement.predicted_scores)
                  .filter(([emotion, score]) => {
                    const original = analysis.emotions[emotion] || 0;
                    return score > original; // Only show improvements
                  })
                  .map(([emotion, score]) => {
                    const original = analysis.emotions[emotion] || 0;
                    const improvement = score - original;
                    return (
                      <div key={emotion} className="p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50">
                        <div className="text-xs text-gray-500 capitalize mb-2 font-medium">{emotion}</div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            {Math.round(original)}%
                          </span>
                          <span className="text-green-600">→</span>
                          <span className="text-xl font-bold text-gray-900">
                            {Math.round(score)}%
                          </span>
                        </div>
                        <Badge className="bg-green-600 text-white text-xs mt-2">
                          +{Math.round(improvement)}%
                        </Badge>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Platform-Specific Tips */}
          {enhancement.platform_tips && Object.keys(enhancement.platform_tips).length > 0 && (
            <div className={`p-6 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800/90 border-2 border-purple-500 shadow-purple-500/20' : 'bg-white border border-gray-200'}`}>
              <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                📱 Platform-Specific Optimization
              </h4>
              <div className="space-y-3">
                {Object.entries(enhancement.platform_tips).map(([platform, tip]) => (
                  <div key={platform} className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/30 text-gray-200' : 'bg-blue-50 text-gray-800'}`}>
                    <span className={`font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{platform}:</span>
                    <ul className="mt-2 space-y-1">
                      {formatMarkdownText(tip)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TEXT SUPER ENHANCER - Tone Optimizer */}
          {/* Always visible, suggests enhancement first if not done */}
          {analysis?.content_type === 'text' && (
            <div className={`mt-6 p-6 rounded-lg shadow-xl border-4 ${darkMode ? 'bg-gradient-to-br from-blue-900 to-indigo-900 border-blue-400' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-400'} animate-pulse-slow`}>
              {/* Unlock Message Banner */}
              {!enhancement ? (
                <div className={`mb-4 p-4 rounded-lg text-center border-2 ${darkMode ? 'bg-orange-500/20 border-orange-500' : 'bg-orange-100 border-orange-500'}`}>
                  <p className="text-orange-600 dark:text-orange-400 font-bold text-sm mb-2">
                    🔒 Unlock This Feature
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                    Complete <strong>Enhanced Content</strong> first (10 credits above) to unlock Text Tone Optimizer. This feature uses enhancement insights to generate optimized variations.
                  </p>
                </div>
              ) : (
                <div className={`mb-4 p-3 rounded-lg text-center ${darkMode ? 'bg-green-500/20 border-2 border-green-500' : 'bg-green-100 border-2 border-green-500'}`}>
                  <p className="text-green-600 dark:text-green-400 font-bold text-sm">
                    ✨ Feature Unlocked - Ready to Use! ✨
                  </p>
                </div>
              )}
              
              <div className="mb-6">
                <h4 className={`text-xl font-bold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  📝 Text Tone Optimizer
                </h4>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Get your text rewritten in 3 emotional tones optimized for your audience
                </p>
              </div>

              {!textToneEnhancement && (
                <Button
                  onClick={handleTextToneEnhance}
                  disabled={!enhancement || textToneEnhancing || (isDemoAnalysis || analysis?.is_demo_analysis)}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 shadow-lg disabled:opacity-50"
                >
                  {textToneEnhancing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Tone Variations...
                    </>
                  ) : (
                    <>
                      🚀 Generate Text Variations (12 credits)
                    </>
                  )}
                </Button>
              )}

              {textToneEnhancement && (
                <div className="mt-6">
                  {/* Tabs - Mobile Optimized */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    {['professional', 'persuasive', 'empathetic'].map((tone) => (
                      <button
                        key={tone}
                        onClick={() => setActiveTextTab(tone)}
                        className={`flex-1 px-4 sm:px-6 py-3 font-bold rounded-lg transition-all duration-300 capitalize text-sm sm:text-base ${
                          activeTextTab === tone
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/50 border-2 border-blue-400 scale-105'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:border-blue-400 hover:shadow-md'
                        }`}
                      >
                        {tone}
                      </button>
                    ))}
                  </div>

                  {/* Display ALL tone versions - visible tabs shown, others hidden but in DOM for PDF */}
                  <div className="space-y-6">
                    {['professional', 'persuasive', 'empathetic'].map((tone) => (
                      <div 
                        key={tone}
                        className={`p-6 rounded-lg ${darkMode ? 'bg-blue-800/30 border-2 border-blue-600' : 'bg-white border border-blue-300'} ${activeTextTab !== tone ? 'hidden print:block' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <h5 className={`text-lg font-bold capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {tone} Version
                          </h5>
                          {activeTextTab === tone && (
                            <Button
                              onClick={() => handleCopyText(textToneEnhancement.versions[tone], tone)}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              size="sm"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Copy Text
                            </Button>
                          )}
                        </div>
                        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                          <p className={`text-base leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {textToneEnhancement.versions[tone]}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* Emotion Improvements */}
                    <div className={`p-6 rounded-lg ${darkMode ? 'bg-blue-800/30 border-2 border-blue-600' : 'bg-white border border-blue-300'}`}>
                      <h5 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        📊 Emotional Improvements
                      </h5>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {Object.entries(textToneEnhancement.improvements[activeTextTab])
                          .filter(([_, data]) => data.improvement > 0)
                          .sort((a, b) => b[1].improvement - a[1].improvement)
                          .slice(0, 6)
                          .map(([emotion, data]) => (
                            <div key={emotion} className="p-4 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50">
                              <div className="text-xs text-gray-500 capitalize mb-2 font-medium">{emotion}</div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm text-gray-600">{data.original}%</span>
                                <span className="text-green-600">→</span>
                                <span className="text-xl font-bold text-gray-900">{data.enhanced}%</span>
                              </div>
                              <Badge className="bg-green-600 text-white text-xs">
                                +{data.improvement}%
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Original vs Enhanced Comparison */}
                    <div className={`p-6 rounded-lg ${darkMode ? 'bg-blue-800/30 border-2 border-blue-600' : 'bg-white border border-blue-300'}`}>
                      <h5 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Original vs {activeTextTab} Version
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Original:
                          </p>
                          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-100'}`}>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {textToneEnhancement.original_text}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {activeTextTab} Version:
                          </p>
                          <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900/50' : 'bg-blue-50'}`}>
                            <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                              {textToneEnhancement.versions[activeTextTab]}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VIDEO SUPER ENHANCER - Caption Generator */}
          {/* Always visible, suggests enhancement first if not done */}
          {analysis?.content_type === 'video' && (
            <div className={`mt-6 p-6 rounded-lg shadow-xl border-4 ${darkMode ? 'bg-gradient-to-br from-purple-900 to-pink-900 border-purple-400' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-400'} animate-pulse-slow`}>
              {/* Unlock Message Banner */}
              {!enhancement ? (
                <div className={`mb-4 p-4 rounded-lg text-center border-2 ${darkMode ? 'bg-orange-500/20 border-orange-500' : 'bg-orange-100 border-orange-500'}`}>
                  <p className="text-orange-600 dark:text-orange-400 font-bold text-sm mb-2">
                    🔒 Unlock This Feature
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                    Complete <strong>Enhanced Content</strong> first (30 credits above) to unlock Video Caption Generator. This feature uses enhancement insights to create emotional captions.
                  </p>
                </div>
              ) : (
                <div className={`mb-4 p-3 rounded-lg text-center ${darkMode ? 'bg-green-500/20 border-2 border-green-500' : 'bg-green-100 border-2 border-green-500'}`}>
                  <p className="text-green-600 dark:text-green-400 font-bold text-sm">
                    ✨ Feature Unlocked - Ready to Use! ✨
                  </p>
                </div>
              )}
              
              <div className="mb-6">
                <h4 className={`text-xl font-bold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🎬 Video Caption Generator
                </h4>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Generate emotional captions in 3 styles with downloadable SRT files
                </p>
              </div>

              {!videoCaptionEnhancement && (
                <Button
                  onClick={handleVideoCaptionEnhance}
                  disabled={!enhancement || videoCaptionEnhancing || (isDemoAnalysis || analysis?.is_demo_analysis)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-6 py-3 shadow-lg disabled:opacity-50"
                >
                  {videoCaptionEnhancing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Captions...
                    </>
                  ) : (
                    <>
                      🚀 Generate Video Captions (15 credits)
                    </>
                  )}
                </Button>
              )}

              {videoCaptionEnhancement && (
                <div className="mt-6">
                  {/* Tabs - Mobile Optimized */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    {['minimal', 'engaging', 'dramatic'].map((style) => (
                      <button
                        key={style}
                        onClick={() => setActiveVideoTab(style)}
                        className={`flex-1 px-4 sm:px-6 py-3 font-bold rounded-lg transition-all duration-300 capitalize text-sm sm:text-base ${
                          activeVideoTab === style
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50 border-2 border-purple-400 scale-105'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:border-purple-400 hover:shadow-md'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>

                  {/* Display ALL caption styles - visible tabs shown, others hidden but in DOM for PDF */}
                  <div className="space-y-6">
                    {['minimal', 'engaging', 'dramatic'].map((style) => (
                      <div 
                        key={style}
                        className={`p-6 rounded-lg ${darkMode ? 'bg-purple-800/30 border-2 border-purple-600' : 'bg-white border border-purple-300'} ${activeVideoTab !== style ? 'hidden print:block' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h5 className={`text-lg font-bold capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {style} Style
                            </h5>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {videoCaptionEnhancement.caption_styles[style]?.title}
                            </p>
                          </div>
                          {activeVideoTab === style && (
                            <Button
                              onClick={() => handleDownloadSRT(videoCaptionEnhancement.srt_files[style], style)}
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                              size="sm"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download SRT
                            </Button>
                          )}
                        </div>
                        
                        {/* Caption Preview */}
                        <div className="space-y-3">
                          {videoCaptionEnhancement.caption_styles[style]?.captions.map((caption, idx) => (
                            <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                              <div className="flex items-start gap-3">
                                <Badge className="bg-purple-600 text-white">{idx + 1}</Badge>
                                <p className={`text-base flex-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                  {caption}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Emotional Peaks */}
                    {videoCaptionEnhancement.emotional_peaks && videoCaptionEnhancement.emotional_peaks.length > 0 && (
                      <div className={`p-6 rounded-lg ${darkMode ? 'bg-purple-800/30 border-2 border-purple-600' : 'bg-white border border-purple-300'}`}>
                        <h5 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          🎯 Key Emotional Moments
                        </h5>
                        <div className="space-y-3">
                          {videoCaptionEnhancement.emotional_peaks.slice(0, 5).map((peak, idx) => (
                            <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className={`font-semibold capitalize ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                                    {peak.dominant_emotion}
                                  </span>
                                  <span className={`text-sm ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    at {peak.timestamp}s
                                  </span>
                                </div>
                                <Badge className="bg-purple-600 text-white">
                                  Intensity: {Math.round(peak.intensity * 100)}%
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* SRT Preview */}
                    <div className={`p-6 rounded-lg ${darkMode ? 'bg-purple-800/30 border-2 border-purple-600' : 'bg-white border border-purple-300'}`}>
                      <h5 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        📄 SRT File Preview
                      </h5>
                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'} overflow-x-auto`}>
                        <pre className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap`}>
                          {videoCaptionEnhancement.srt_files[activeVideoTab].substring(0, 500)}...
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* IMAGE SUPER ENHANCER - Photo Enhancement & AI Creative */}
          {/* Always visible, suggests enhancement first if not done */}
          {analysis?.content_type === 'image' && (
            <div className={`mt-6 p-6 rounded-lg shadow-xl border-4 ${darkMode ? 'bg-gradient-to-br from-indigo-900 to-purple-900 border-indigo-400' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-400'} animate-pulse-slow`}>
              {/* Unlock Message Banner */}
              {!enhancement ? (
                <div className={`mb-4 p-4 rounded-lg text-center border-2 ${darkMode ? 'bg-orange-500/20 border-orange-500' : 'bg-orange-100 border-orange-500'}`}>
                  <p className="text-orange-600 dark:text-orange-400 font-bold text-sm mb-2">
                    🔒 Unlock These Features
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                    Complete <strong>Enhanced Content</strong> first (10 credits above) to unlock Super Enhancers. These features use enhancement insights to create optimized images.
                  </p>
                </div>
              ) : (
                <div className={`mb-4 p-3 rounded-lg text-center ${darkMode ? 'bg-green-500/20 border-2 border-green-500' : 'bg-green-100 border-2 border-green-500'}`}>
                  <p className="text-green-600 dark:text-green-400 font-bold text-sm">
                    ✨ Features Unlocked - Choose Your Style! ✨
                  </p>
                </div>
              )}
              
              <div className="mb-6">
                <h4 className={`text-xl font-bold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  🚀 Super Enhancer Options
                </h4>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Choose your enhancement style: Real photo editing or AI creative reimagining
                </p>
              </div>

              {/* Two Enhancement Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Photo Enhancement Option */}
                <div className={`p-4 rounded-lg border-2 ${darkMode ? 'bg-purple-900/30 border-purple-500' : 'bg-white border-purple-300'}`}>
                  <h5 className={`font-bold text-lg mb-2 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    📸 Photo Enhancement
                  </h5>
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Real photo editing that keeps your exact people, products, and elements
                  </p>
                  <ul className={`text-xs mb-4 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <li>✓ Same subjects & content</li>
                    <li>✓ Enhanced lighting & colors</li>
                    <li>✓ Professional quality</li>
                  </ul>
                  {!photoEnhancement && (
                    <Button
                      onClick={handlePhotoEnhance}
                      disabled={!enhancement || photoEnhancing || (isDemoAnalysis || analysis?.is_demo_analysis)}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-4 py-2 disabled:opacity-50"
                    >
                      {photoEnhancing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enhancing...
                        </>
                      ) : (
                        <>
                          Generate (15 credits)
                        </>
                      )}
                    </Button>
                  )}
                  {photoEnhancement && (
                    <Button
                      onClick={() => {
                        setActiveImageTab('photo');
                        // Scroll to results after a brief delay
                        setTimeout(() => {
                          const resultsElement = document.getElementById('super-enhancer-results');
                          if (resultsElement) {
                            resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 flex items-center justify-center gap-2"
                    >
                      ✓ View Results
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </Button>
                  )}
                </div>

                {/* AI Creative Option */}
                <div className={`p-4 rounded-lg border-2 ${darkMode ? 'bg-indigo-900/30 border-indigo-500' : 'bg-white border-indigo-300'}`}>
                  <h5 className={`font-bold text-lg mb-2 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    🎨 EmoQ AI Creative
                  </h5>
                  <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Artistic AI reimagining for creative concepts and marketing
                  </p>
                  <ul className={`text-xs mb-4 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <li>✓ Creative interpretation</li>
                    <li>✓ Marketing concepts</li>
                    <li>✓ Artistic vision</li>
                  </ul>
                  {!creativeEnhancement && (
                    <Button
                      onClick={handleCreativeEnhance}
                      disabled={!enhancement || creativeEnhancing || (isDemoAnalysis || analysis?.is_demo_analysis)}
                      className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-4 py-2 disabled:opacity-50"
                    >
                      {creativeEnhancing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          Generate (20 credits)
                        </>
                      )}
                    </Button>
                  )}
                  {creativeEnhancement && (
                    <Button
                      onClick={() => {
                        setActiveImageTab('creative');
                        // Scroll to results after a brief delay
                        setTimeout(() => {
                          const resultsElement = document.getElementById('super-enhancer-results');
                          if (resultsElement) {
                            resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 flex items-center justify-center gap-2"
                    >
                      ✓ View Results
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </Button>
                  )}
                </div>
              </div>

              {/* Results Display with Tabs */}
              {(photoEnhancement || creativeEnhancement) && (
                <div className="mt-6" id="super-enhancer-results">
                  {/* Enhanced Tabs with Glow - Mobile Optimized */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    {photoEnhancement && (
                      <button
                        onClick={() => setActiveImageTab('photo')}
                        className={`flex-1 px-4 sm:px-6 py-3 font-bold rounded-lg transition-all duration-300 text-sm sm:text-base ${
                          activeImageTab === 'photo'
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/50 border-2 border-purple-400 scale-105'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:border-purple-400 hover:shadow-md'
                        }`}
                      >
                        📸 Photo Enhanced
                      </button>
                    )}
                    {creativeEnhancement && (
                      <button
                        onClick={() => setActiveImageTab('creative')}
                        className={`flex-1 px-4 sm:px-6 py-3 font-bold rounded-lg transition-all duration-300 text-sm sm:text-base ${
                          activeImageTab === 'creative'
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/50 border-2 border-indigo-400 scale-105'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:border-indigo-400 hover:shadow-md'
                        }`}
                      >
                        🎨 EmoQ AI Creative
                      </button>
                    )}
                  </div>

                  {/* Photo Enhancement Results */}
                  {activeImageTab === 'photo' && photoEnhancement && (
                    <div className="space-y-6">
                      {/* Before/After Comparison - Mobile Optimized */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                        <div>
                          <h5 className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Original:
                          </h5>
                          <img 
                            src={analysis.image_data.startsWith('data:') ? analysis.image_data : `data:image/jpeg;base64,${analysis.image_data}`}
                            alt="Original" 
                            className="w-full rounded-lg border-2 border-gray-300"
                          />
                        </div>
                        <div>
                          <h5 className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Photo Enhanced:
                          </h5>
                          <img 
                            src={photoEnhancement.enhanced_image}
                            alt="Photo Enhanced" 
                            className="w-full rounded-lg border-2 border-purple-500 shadow-lg"
                          />
                        </div>
                      </div>

                      {/* Improvements - Mobile Optimized */}
                      <div className={`p-3 sm:p-6 rounded-lg ${darkMode ? 'bg-purple-800/30 border-2 border-purple-600' : 'bg-white border border-purple-300'}`}>
                        <h5 className={`text-sm sm:text-lg font-bold mb-2 sm:mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          📊 Improvement Analytics
                        </h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                          {Object.entries(photoEnhancement.improvements)
                            .filter(([_, data]) => data.improvement > 0)
                            .sort((a, b) => b[1].improvement - a[1].improvement)
                            .map(([emotion, data]) => (
                              <div key={emotion} className="p-2 sm:p-4 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50">
                                <div className="text-[10px] sm:text-xs text-gray-500 capitalize mb-1 sm:mb-2 font-medium leading-tight truncate">{emotion}</div>
                                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                                  <span className="text-[10px] sm:text-sm text-gray-600">{data.original}%</span>
                                  <span className="text-green-600 text-xs sm:text-base">→</span>
                                  <span className="text-sm sm:text-xl font-bold text-gray-900">{data.enhanced}%</span>
                                </div>
                                <Badge className="bg-green-600 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
                                  +{data.improvement}%
                                </Badge>
                              </div>
                            ))}
                        </div>
                        <div className="mt-2 sm:mt-4 p-2 sm:p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg">
                          <p className="text-center text-xs sm:text-base font-bold text-gray-900">
                            Avg Improvement: <span className="text-green-600 text-sm sm:text-xl">+{photoEnhancement.average_improvement}%</span>
                          </p>
                        </div>
                      </div>

                      {/* Edit Instructions */}
                      {photoEnhancement.edit_instructions && (
                        <div className={`p-6 rounded-lg ${darkMode ? 'bg-gradient-to-br from-blue-900/40 to-purple-900/40 border-2 border-blue-500' : 'bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-300'}`}>
                          <h5 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            📱 Replicate These Edits on Your Phone
                          </h5>
                          
                          {/* Tabs for platforms */}
                          <div className="flex gap-2 mb-4">
                            {['iphone', 'android', 'photoshop'].map(platform => (
                              <button
                                key={platform}
                                onClick={() => setActiveEditPlatform(platform)}
                                className={`px-4 py-2 rounded-lg font-semibold capitalize transition-all ${
                                  activeEditPlatform === platform
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : darkMode 
                                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                      : 'bg-white text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                {platform === 'iphone' ? 'iPhone' : platform === 'android' ? 'Android' : 'Photoshop'}
                              </button>
                            ))}
                          </div>

                          {/* Instructions */}
                          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800/50' : 'bg-white'}`}>
                            <ol className="space-y-2 list-decimal list-inside">
                              {photoEnhancement.edit_instructions[activeEditPlatform || 'iphone'].map((instruction, idx) => (
                                <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {instruction}
                                </li>
                              ))}
                            </ol>
                          </div>

                          {/* Quick Settings Summary */}
                          {photoEnhancement.edit_instructions.settings_summary && (
                            <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                              <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-green-400' : 'text-green-800'}`}>
                                ⚡ Quick Settings:
                              </p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {Object.entries(photoEnhancement.edit_instructions.settings_summary).map(([key, value]) => (
                                  <div key={key} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <span className="font-semibold capitalize">{key.replace('_', ' ')}:</span> {value}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Note: Download removed - use instructions above to recreate in your photo editor */}
                      <div className={`text-center p-4 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                          💡 Use the instructions above to recreate these edits in your phone's photo editor for best quality
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Creative Enhancement Results */}
                  {activeImageTab === 'creative' && creativeEnhancement && (
                    <div className="space-y-6">
                      {/* Before/After Comparison - Mobile Optimized */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                        <div>
                          <h5 className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Original:
                          </h5>
                          <img 
                            src={analysis.image_data.startsWith('data:') ? analysis.image_data : `data:image/jpeg;base64,${analysis.image_data}`}
                            alt="Original" 
                            className="w-full rounded-lg border-2 border-gray-300"
                          />
                        </div>
                        <div>
                          <h5 className={`text-xs sm:text-sm font-semibold mb-1 sm:mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            EmoQ AI Creative:
                          </h5>
                          <img 
                            src={creativeEnhancement.enhanced_image}
                            alt="AI Creative" 
                            className="w-full rounded-lg border-2 border-indigo-500 shadow-lg"
                          />
                        </div>
                      </div>

                      {/* Improvements - Mobile Optimized */}
                      <div className={`p-3 sm:p-6 rounded-lg ${darkMode ? 'bg-indigo-800/30 border-2 border-indigo-600' : 'bg-white border border-indigo-300'}`}>
                        <h5 className={`text-sm sm:text-lg font-bold mb-2 sm:mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          📊 Improvement Analytics
                        </h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                          {Object.entries(creativeEnhancement.improvements)
                            .filter(([_, data]) => data.improvement > 0)
                            .sort((a, b) => b[1].improvement - a[1].improvement)
                            .map(([emotion, data]) => (
                              <div key={emotion} className="p-2 sm:p-4 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50">
                                <div className="text-[10px] sm:text-xs text-gray-500 capitalize mb-1 sm:mb-2 font-medium leading-tight truncate">{emotion}</div>
                                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                                  <span className="text-[10px] sm:text-sm text-gray-600">{data.original}%</span>
                                  <span className="text-green-600 text-xs sm:text-base">→</span>
                                  <span className="text-sm sm:text-xl font-bold text-gray-900">{data.enhanced}%</span>
                                </div>
                                <Badge className="bg-green-600 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
                                  +{data.improvement}%
                                </Badge>
                              </div>
                            ))}
                        </div>
                        <div className="mt-2 sm:mt-4 p-2 sm:p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg">
                          <p className="text-center text-xs sm:text-base font-bold text-gray-900">
                            Avg Improvement: <span className="text-green-600 text-sm sm:text-xl">+{creativeEnhancement.average_improvement}%</span>
                          </p>
                        </div>
                      </div>

                      {/* Download - Mobile Optimized */}
                      <div className="flex justify-center">
                        <a
                          href={creativeEnhancement.enhanced_image}
                          download={`ai-creative-${analysis.id}.png`}
                          className="inline-flex items-center gap-1 sm:gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-3 py-2 sm:px-6 sm:py-3 rounded-lg shadow-lg text-xs sm:text-base"
                        >
                          <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="hidden xs:inline">Download AI Creative Image</span>
                          <span className="xs:hidden">Download</span>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!photoEnhancement && !creativeEnhancement && !photoEnhancing && !creativeEnhancing && (
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-indigo-800/30 border-indigo-600' : 'bg-white/80 border-indigo-300'} border`}>
                  <div className="flex items-start gap-3">
                    <Sparkles className={`w-5 h-5 flex-shrink-0 mt-1 ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`} />
                    <div>
                      <h5 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        What you'll get:
                      </h5>
                      <ul className={`space-y-2 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <li>🎨 AI-generated enhanced version of your image</li>
                        <li>📊 Before/after comparison with improvement metrics</li>
                        <li>📈 Emotion score improvements for each category</li>
                        <li>💾 High-resolution download ready</li>
                        <li>✨ All enhancement suggestions automatically applied</li>
                      </ul>
                      <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-indigo-600' : 'border-indigo-300'}`}>
                        <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <AlertCircle className="w-3 h-3 inline mr-1" />
                          {(isDemoAnalysis || analysis?.is_demo_analysis) ? (
                            <>
                              <span className="font-semibold text-yellow-500">Demo mode</span> • Super Enhancer not available for demos
                            </>
                          ) : (
                            <>
                              Cost: <span className="font-semibold">20 credits</span> • 
                              {analysis?.testing_mode ? ' Testing mode active' : ' High-quality AI image generation'}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AIEnhancement;
